/**
 * BrowsePanel — SharePoint folder browser for the Outlook Add-in
 *
 * Ported from vanilla JS: 09_browse.js, 10_browse_search.js
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  ArrowLeft, ChevronRight, Folder, HardDrive, Globe, Search, Loader2, X, Mail, AlertCircle
} from 'lucide-react';
import { errorMessage } from '../../../utils/errorMessage';

// Format an error for display in the panel. Prefers the host-page
// window.errorMessage(err, fallback) helper (installed by outlook-app.js)
// and falls back to a manual extraction otherwise. Never returns
// [object Object] or a stringified Error.
function _formatError(err, fallback) {
  if (typeof window !== 'undefined' && typeof window.errorMessage === 'function') {
    try {
      return window.errorMessage(err, fallback);
    } catch (_) {
      /* fall through */
    }
  }
  if (typeof err === 'string') return err || fallback;
  return errorMessage(err, fallback);
}

const SEARCH_SOURCES = [
  { key: 'sharepoint', label: 'SP' },
  { key: 'outlook', label: 'Outlook' },
  { key: 'both', label: 'Both' },
];

export default function BrowsePanel({ userId, bonsaiApi, onSelectFolder, onClose, onBatchFile, onFileHere }) {
  const [navStack, setNavStack] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [currentLabel, setCurrentLabel] = useState('SharePoint');
  const [approvedDriveId, setApprovedDriveId] = useState('');
  const [searchSource, setSearchSource] = useState('sharepoint');
  const [outlookResults, setOutlookResults] = useState([]);
  const [dragOverId, setDragOverId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  // Inline error banner for failed folder/drive/site fetches. Without this,
  // backend 4xx/5xx renders as an empty "No items found" panel and the user
  // cannot tell the click was registered at all.
  const [loadError, setLoadError] = useState(null);
  const searchTimerRef = useRef(null);

  useEffect(() => { loadLocations(); }, []);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setNavStack([]);
    setCurrentLabel('SharePoint');
    setLoadError(null);
    try {
      const [approvedResp, sitesResp, recentResp] = await Promise.all([
        bonsaiApi.get('/sharepoint/approved-site').catch(() => ({ data: {} })),
        bonsaiApi.get('/sharepoint/sites').catch(() => ({ data: { items: [] } })),
        bonsaiApi.get('/sharepoint/recent?limit=5').catch(() => ({ data: { items: [] } })),
      ]);
      const approved = approvedResp.data || {};
      const sites = sitesResp.data?.items || [];
      const recent = recentResp.data?.items || [];
      const drives = approved.drives || [];
      const defaultDriveId = approved.drive_id || '';
      setApprovedDriveId(defaultDriveId);

      const allItems = [];
      // Approved drives
      drives.forEach(d => {
        allItems.push({
          type: 'drive', id: d.id, name: d.name, driveId: d.id,
          isDefault: d.id === defaultDriveId,
          section: 'Matter Document Libraries',
        });
      });
      // Recent
      recent.forEach(f => {
        allItems.push({ type: 'recent', name: f.name, webUrl: f.webUrl, section: 'Recent' });
      });
      // Sites
      sites.forEach(s => {
        allItems.push({ type: 'site', id: s.id, name: s.name, section: 'All SharePoint Sites' });
      });
      setItems(allItems);
    } catch (err) {
      console.error('[BrowsePanel] loadLocations failed', err);
      setItems([]);
      setLoadError({
        message: _formatError(err, 'Could not load SharePoint locations'),
        retry: () => loadLocations(),
      });
    }
    setLoading(false);
  }, [bonsaiApi]);

  const loadDrives = useCallback(async (siteId, siteName) => {
    setLoading(true);
    setNavStack(prev => [...prev, { label: currentLabel, items, type: 'root' }]);
    setCurrentLabel(siteName);
    setLoadError(null);
    try {
      const resp = await bonsaiApi.get(`/sharepoint/sites/${siteId}/drives`);
      const drives = resp.data?.drives || [];
      setItems(drives.map(d => ({ type: 'drive', id: d.id, name: d.name, driveId: d.id })));
    } catch (err) {
      console.error('[BrowsePanel] loadDrives failed', { siteId, err });
      setItems([]);
      setLoadError({
        message: _formatError(err, `Could not load drives for "${siteName}"`),
        retry: () => loadDrives(siteId, siteName),
      });
    }
    setLoading(false);
  }, [bonsaiApi, currentLabel, items]);

  const loadFolder = useCallback(async (driveId, folderId, label) => {
    setLoading(true);
    setNavStack(prev => [...prev, { label: currentLabel, items, type: 'folder' }]);
    setCurrentLabel(label);
    setCurrentFolder({ driveId, itemId: folderId, name: label });
    setLoadError(null);
    try {
      const endpoint = folderId === 'root'
        ? `/sharepoint/drives/${driveId}/children`
        : `/sharepoint/drives/${driveId}/children?item_id=${folderId}`;
      const resp = await bonsaiApi.get(endpoint);
      const children = resp.data?.subfolders || resp.data?.children || resp.data?.items || [];
      setItems(children.map(c => ({
        type: (c.isFolder || c.folder || c.type === 'folder') ? 'folder' : 'file',
        id: c.id, name: c.name, driveId: driveId,
        webUrl: c.webUrl, childCount: c.folder?.childCount || c.childCount || 0,
      })));
    } catch (err) {
      console.error('[BrowsePanel] loadFolder failed', { driveId, folderId, err });
      setItems([]);
      setLoadError({
        message: _formatError(err, 'Could not load folder contents'),
        retry: () => loadFolder(driveId, folderId, label),
      });
    }
    setLoading(false);
  }, [bonsaiApi, currentLabel, items]);

  const goBack = useCallback(() => {
    if (navStack.length === 0) return;
    const prev = navStack[navStack.length - 1];
    setNavStack(s => s.slice(0, -1));
    setCurrentLabel(prev.label);
    setItems(prev.items);
    // Clear currentFolder when going back to root level
    if (navStack.length <= 1) {
      setCurrentFolder(null);
    }
  }, [navStack]);

  const handleItemClick = useCallback((item) => {
    if (item.type === 'drive') {
      loadFolder(item.driveId, 'root', item.name);
    } else if (item.type === 'site') {
      loadDrives(item.id, item.name);
    } else if (item.type === 'folder') {
      loadFolder(item.driveId, item.id, item.name);
    } else if (item.type === 'recent') {
      if (item.webUrl) window.open(item.webUrl, '_blank');
    }
  }, [loadFolder, loadDrives]);

  const selectAsDestination = useCallback((item) => {
    if (onFileHere) {
      onFileHere({
        driveId: item.driveId,
        itemId: item.id || 'root',
        name: item.name,
        reference: item.name,
        webUrl: item.webUrl || '',
      });
    } else {
      onSelectFolder?.({
        type: 'browse',
        driveId: item.driveId,
        itemId: item.id || 'root',
        name: item.name,
        webUrl: item.webUrl || '',
      });
      onClose?.();
    }
  }, [onSelectFolder, onClose, onFileHere]);

  const breadcrumb = navStack.map(s => s.label).concat(currentLabel).join(' / ');

  // Shared dragover handler — MUST preventDefault on every drag event in the
  // panel; otherwise the browser's default "open dropped item" kicks in and
  // dragging an email onto the panel just navigates away from the addin
  // (regression symptom: drop opens the email in a new screen).
  const allowDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      try { e.dataTransfer.dropEffect = 'copy'; } catch {}
    }
  }, []);

  // No-op drop handler for non-folder areas (header, padding, section labels).
  // Without this, even with allowDrop on dragover, the browser's default drop
  // behaviour fires when the drop lands on a non-target child element.
  const swallowDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
  }, []);

  const handleFolderDrop = useCallback(async (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);

    const onError = (msg) => {
      try { onBatchFile?.({ __error: msg }); } catch {}
    };

    if (typeof Office === 'undefined' || !Office.context?.mailbox) {
      onError('Outlook context unavailable — cannot determine selected email');
      return;
    }

    if (Office.context.mailbox.getSelectedItemsAsync) {
      Office.context.mailbox.getSelectedItemsAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.length > 0) {
          const emailIds = result.value.map(item => item.itemId).filter(Boolean);
          if (emailIds.length === 0) {
            onError('No email IDs available from selection');
            return;
          }
          onBatchFile?.({
            emailIds,
            driveId: folder.driveId,
            folderId: folder.id,
            folderName: folder.name,
            breadcrumb,
          });
        } else {
          onError(result.error?.message || 'Could not read selected emails from Outlook');
        }
      });
    } else {
      // Fallback: file the currently-open email (older Outlook clients).
      onBatchFile?.({
        emailIds: null,
        driveId: folder.driveId,
        folderId: folder.id,
        folderName: folder.name,
        breadcrumb,
      });
    }
  }, [onBatchFile, breadcrumb]);

  const searchSharePoint = useCallback(async (q) => {
    try {
      const resp = await bonsaiApi.get(`/sharepoint/search?q=${encodeURIComponent(q)}&limit=10`);
      return resp.data?.results || resp.data?.items || [];
    } catch { return []; }
  }, [bonsaiApi]);

  const searchOutlook = useCallback(async (q) => {
    try {
      const resp = await axios.get(
        `https://bonsaimail.azurewebsites.net/api/bonsai/mail/search?q=${encodeURIComponent(q)}&limit=10`,
        { headers: { 'X-Bonsai-User-Id': userId } }
      );
      return resp.data?.results || resp.data?.items || resp.data || [];
    } catch { return []; }
  }, [userId]);

  const handleSearch = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (q.length < 2) { setSearchResults([]); setOutlookResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      if (searchSource === 'sharepoint') {
        setSearchResults(await searchSharePoint(q));
        setOutlookResults([]);
      } else if (searchSource === 'outlook') {
        setSearchResults([]);
        setOutlookResults(await searchOutlook(q));
      } else {
        const [sp, ol] = await Promise.all([searchSharePoint(q), searchOutlook(q)]);
        setSearchResults(sp);
        setOutlookResults(ol);
      }
      setSearching(false);
    }, 400);
  }, [bonsaiApi, searchSource, searchSharePoint, searchOutlook]);

  // Group items by section
  const sections = {};
  items.forEach(item => {
    const sec = item.section || 'Items';
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(item);
  });

  return (
    <div className="absolute inset-0 bg-navy-card/95 z-50 flex flex-col overflow-hidden" data-testid="browse-panel"
      onDragEnter={allowDrop}
      onDragOver={allowDrop}
      onDrop={swallowDrop}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {navStack.length > 0 && (
            <button onClick={goBack} className="text-blue-400 hover:text-blue-300 p-0.5" data-testid="browse-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <Globe className="w-4 h-4 text-teal shrink-0" />
          <span className="text-sm font-semibold text-white truncate">{currentLabel}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {currentFolder && onFileHere && (
            <button
              onClick={() => selectAsDestination({ driveId: currentFolder.driveId, id: currentFolder.itemId, name: currentFolder.name })}
              className="text-[9px] text-teal hover:text-teal px-2 py-1 rounded border border-teal/40 bg-teal-dim/20 hover:bg-teal-dim/40 transition-colors whitespace-nowrap"
              data-testid="browse-file-here-header-btn">
              File here
            </button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="browse-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search source toggle + Search */}
      <div className="px-3 py-2 border-b border-gray-700/50 space-y-2">
        <div className="flex rounded-md overflow-hidden border border-gray-700 w-fit" data-testid="search-source-toggle">
          {SEARCH_SOURCES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => { setSearchSource(key); setSearchResults([]); setOutlookResults([]); setSearchQuery(''); }}
              className={`px-3 py-1 text-[10px] font-semibold transition-colors ${
                searchSource === key
                  ? 'bg-teal text-gray-900'
                  : 'bg-gray-800 text-gray-400 hover:text-gray-200'
              }`}
              data-testid={`search-source-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <Input value={searchQuery} onChange={handleSearch}
            placeholder={searchSource === 'outlook' ? 'Search Outlook inbox...' : searchSource === 'both' ? 'Search SharePoint & Outlook...' : 'Search SharePoint...'}
            className="pl-8 h-8 bg-gray-800 border-gray-700 text-xs text-white" data-testid="browse-search-input" />
          {searching && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400 animate-spin" />}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3" data-testid="browse-scroll"
        onDragEnter={allowDrop} onDragOver={allowDrop} onDrop={swallowDrop}>
        {/* SharePoint Search Results */}
        {searchQuery.length >= 2 && searchResults.length > 0 && (
          <div className="mb-3" data-testid="browse-search-results">
            <div className="text-xs font-semibold text-blue-400 mb-1.5">
              {searchSource === 'both' ? 'SharePoint' : 'Search results'}
            </div>
            {searchResults.map((r, i) => {
              const searchFolder = { type: 'folder', driveId: r.driveId || r.drive_id, id: r.id, name: r.name };
              return (
              <div key={i} onClick={() => handleItemClick(searchFolder)}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800/40 transition-colors ${
                  dragOverId === r.id ? 'bg-teal-dim/40 ring-1 ring-teal/60' : ''
                }`} data-testid={`search-result-${i}`}
                onDragEnter={(e) => { allowDrop(e); setDragOverId(r.id); }}
                onDragOver={(e) => { allowDrop(e); setDragOverId(r.id); }}
                onDragLeave={() => setDragOverId(null)}
                onDrop={(e) => handleFolderDrop(e, searchFolder)}>
                <Folder className="w-3.5 h-3.5 text-status-amber/70" />
                <span className="text-xs text-gray-300 truncate flex-1">{r.name}</span>
              </div>
              );
            })}
          </div>
        )}

        {/* Divider between sections in "Both" mode */}
        {searchSource === 'both' && searchQuery.length >= 2 && searchResults.length > 0 && outlookResults.length > 0 && (
          <div className="border-t border-gray-700/50 my-2" />
        )}

        {/* Outlook Search Results */}
        {searchQuery.length >= 2 && outlookResults.length > 0 && (
          <div className="mb-3" data-testid="browse-outlook-results">
            <div className="text-xs font-semibold text-purple-400 mb-1.5">
              {searchSource === 'both' ? 'Outlook Inbox' : 'Search results'}
            </div>
            {outlookResults.map((r, i) => (
              <div key={i}
                onClick={() => r.webUrl && window.open(r.webUrl, '_blank')}
                className={`flex items-start gap-2 p-2 rounded transition-colors ${r.webUrl ? 'cursor-pointer hover:bg-gray-800/40' : ''}`}
                data-testid={`outlook-result-${i}`}>
                <Mail className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-200 truncate">{r.subject || r.name || '(no subject)'}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {r.from?.emailAddress?.name || r.from?.name || r.from || ''}
                    {(r.receivedDateTime || r.date) && ` · ${new Date(r.receivedDateTime || r.date).toLocaleDateString()}`}
                  </div>
                  {(r.bodyPreview || r.preview) && (
                    <div className="text-[10px] text-gray-600 truncate mt-0.5">{r.bodyPreview || r.preview}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-blue-400 animate-spin" /></div>
        )}

        {/* Inline load-error banner (folder/drive/site fetch failed) */}
        {!loading && loadError && (
          <div
            className="mb-3 p-2.5 rounded border border-red-500/40 bg-red-900/20 text-xs text-red-200"
            data-testid="browse-load-error"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-red-300 mb-0.5">Could not load folder contents</div>
                <div className="text-red-200/90 break-words" data-testid="browse-load-error-detail">{loadError.message}</div>
              </div>
              <button
                onClick={() => { const r = loadError.retry; if (r) r(); }}
                className="text-[10px] font-semibold text-red-100 hover:text-white px-2 py-1 rounded border border-red-400/50 hover:border-red-300 shrink-0"
                data-testid="browse-load-error-retry"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Items */}
        {!loading && navStack.length === 0 && Object.keys(sections).map(sec => (
          <div key={sec} className="mb-3" data-testid={`browse-section-${sec}`}>
            <div className="text-xs font-semibold text-teal mb-1.5">{sec}</div>
            {sections[sec].map((item, i) => (
              <div key={i} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800/40 transition-colors ${item.isDefault ? 'bg-teal-dim/20 border border-teal/30' : ''} ${
                item.type === 'drive' && dragOverId === item.id ? 'bg-teal-dim/40 ring-1 ring-teal/60' : ''
              }`}
                onClick={() => handleItemClick(item)} data-testid={`browse-item-${i}`}
                onDragEnter={item.type === 'drive' ? (e) => { allowDrop(e); setDragOverId(item.id); } : undefined}
                onDragOver={item.type === 'drive' ? (e) => { allowDrop(e); setDragOverId(item.id); } : undefined}
                onDragLeave={item.type === 'drive' ? () => setDragOverId(null) : undefined}
                onDrop={item.type === 'drive' ? (e) => handleFolderDrop(e, { ...item, id: 'root' }) : undefined}>
                {item.type === 'drive' && <HardDrive className="w-3.5 h-3.5 text-teal" />}
                {item.type === 'site' && <Globe className="w-3.5 h-3.5 text-blue-400" />}
                {item.type === 'recent' && <Folder className="w-3.5 h-3.5 text-gray-400" />}
                <span className="text-xs text-gray-300 truncate flex-1">{item.name}</span>
                {item.isDefault && <span className="text-[9px] text-teal">(default)</span>}
                {(item.type === 'drive' || item.type === 'site') && <ChevronRight className="w-3 h-3 text-gray-600" />}
              </div>
            ))}
          </div>
        ))}

        {/* Folder contents (when navigating) */}
        {!loading && navStack.length > 0 && items.map((item, i) => (
          <div key={i}
            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-800/40 transition-colors group ${
              item.type === 'folder' && dragOverId === item.id ? 'bg-teal-dim/40 ring-1 ring-teal/60' : ''
            }`}
            data-testid={`browse-folder-${i}`}
            onDragEnter={item.type === 'folder' ? (e) => { allowDrop(e); setDragOverId(item.id); } : undefined}
            onDragOver={item.type === 'folder' ? (e) => { allowDrop(e); setDragOverId(item.id); } : undefined}
            onDragLeave={item.type === 'folder' ? () => setDragOverId(null) : undefined}
            onDrop={item.type === 'folder' ? (e) => handleFolderDrop(e, item) : undefined}
          >
            <div className="flex-1 flex items-center gap-2 min-w-0" onClick={() => item.type === 'folder' ? handleItemClick(item) : null}>
              <Folder className={`w-3.5 h-3.5 ${item.type === 'folder' ? 'text-status-amber/70' : 'text-gray-500'}`} />
              <span className="text-xs text-gray-300 truncate">{item.name}</span>
              {item.type === 'folder' && item.childCount > 0 && <ChevronRight className="w-3 h-3 text-gray-600" />}
            </div>
            {item.type === 'folder' && (
              <button onClick={(e) => { e.stopPropagation(); selectAsDestination(item); }}
                className="text-[9px] text-teal hover:text-teal px-1.5 py-0.5 rounded border border-teal/40 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`browse-select-${i}`}>
                File here
              </button>
            )}
          </div>
        ))}

        {!loading && items.length === 0 && (
          <div className="text-center py-4 text-xs text-gray-500">No items found</div>
        )}
      </div>
    </div>
  );
}
